import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ApplicationSetting, FieldSetting, HistoryTrackingUnit, GlobalSettings, UIField, CalculatedValueDefinition } from '../types/applicationSettings';
import { apiService } from '../services/api';
import type { AppDispatch } from './index';

export type { ApplicationSetting };

interface ApplicationSettingsState {
  settings: ApplicationSetting[];
  fieldLists: Record<string, FieldSetting[]>;
  historyTrackingUnitMap: Record<string, HistoryTrackingUnit>;
  globalSettings: GlobalSettings | null;
  uiFields: UIField[];
  calculatedValueDefinitions: CalculatedValueDefinition[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ApplicationSettingsState = {
  settings: [],
  fieldLists: {},
  historyTrackingUnitMap: {},
  globalSettings: null,
  uiFields: [],
  calculatedValueDefinitions: [],
  isLoading: false,
  error: null,
};

const applicationSettingsSlice = createSlice({
  name: 'applicationSettings',
  initialState,
  reducers: {
    setSettings(state, action: PayloadAction<ApplicationSetting[]>) {
      state.settings = action.payload;
    },
    setFieldLists(state, action: PayloadAction<Record<string, FieldSetting[]>>) {
      state.fieldLists = action.payload;
    },
    setFieldListForSetting(state, action: PayloadAction<{ settingId: string; fieldList: FieldSetting[] }>) {
      state.fieldLists[action.payload.settingId] = action.payload.fieldList;
    },
    setHistoryTrackingUnitMap(state, action: PayloadAction<Record<string, HistoryTrackingUnit>>) {
      state.historyTrackingUnitMap = action.payload;
    },
    setHistoryTrackingUnitForSetting(state, action: PayloadAction<{ settingId: string; value: HistoryTrackingUnit }>) {
      state.historyTrackingUnitMap[action.payload.settingId] = action.payload.value;
    },
    addSetting(state, action: PayloadAction<ApplicationSetting>) {
      state.settings.push(action.payload);
    },
    updateSetting(state, action: PayloadAction<ApplicationSetting>) {
      const idx = state.settings.findIndex((s) => s.settingId === action.payload.settingId);
      if (idx !== -1) {
        state.settings[idx] = action.payload;
      } else {
        state.settings.push(action.payload);
      }
    },
    removeSetting(state, action: PayloadAction<string>) {
      state.settings = state.settings.filter((s) => s.settingId !== action.payload);
    },
    setGlobalSettings(state, action: PayloadAction<GlobalSettings | null>) {
      state.globalSettings = action.payload;
    },
    setUIFields(state, action: PayloadAction<UIField[]>) {
      state.uiFields = action.payload;
    },
    setCalculatedValueDefinitions(state, action: PayloadAction<CalculatedValueDefinition[]>) {
      state.calculatedValueDefinitions = action.payload;
    },
    addCalculatedValueDefinition(state, action: PayloadAction<CalculatedValueDefinition>) {
      state.calculatedValueDefinitions.push(action.payload);
    },
    updateCalculatedValueDefinition(state, action: PayloadAction<CalculatedValueDefinition>) {
      const idx = state.calculatedValueDefinitions.findIndex((d) => d.id === action.payload.id);
      if (idx !== -1) state.calculatedValueDefinitions[idx] = action.payload;
    },
    removeCalculatedValueDefinition(state, action: PayloadAction<string>) {
      state.calculatedValueDefinitions = state.calculatedValueDefinitions.filter((d) => d.id !== action.payload);
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const {
  setSettings,
  setFieldLists,
  setFieldListForSetting,
  setHistoryTrackingUnitMap,
  setHistoryTrackingUnitForSetting,
  addSetting,
  updateSetting,
  removeSetting,
  setGlobalSettings,
  setUIFields,
  setCalculatedValueDefinitions,
  addCalculatedValueDefinition,
  updateCalculatedValueDefinition,
  removeCalculatedValueDefinition,
  setLoading,
  setError,
} = applicationSettingsSlice.actions;

// Thunks
export const fetchApplicationSettings = () => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const settingsRes = await apiService.listApplicationSettings();
    const list = settingsRes.data ?? [];
    dispatch(setSettings(list));

    const allFieldLists: Record<string, FieldSetting[]> = {};
    for (const s of list) {
      try {
        const blob = s.settingJsonString ? JSON.parse(s.settingJsonString) : {};
        allFieldLists[s.settingId] = blob.fieldSettingList ?? [];
      } catch {
        allFieldLists[s.settingId] = [];
      }
    }
    dispatch(setFieldLists(allFieldLists));
  } catch (err: any) {
    dispatch(setError(err?.message ?? 'Failed to load settings'));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchGlobalSettings = () => async (dispatch: AppDispatch) => {
  try {
    const res = await apiService.getGlobalApplicationSettings();
    dispatch(setGlobalSettings(res.data));
  } catch {
    // non-fatal
  }
};

export const fetchUIFields = () => async (dispatch: AppDispatch) => {
  try {
    const res = await apiService.getUIFields();
    dispatch(setUIFields(res.data ?? []));
  } catch {
    // non-fatal
  }
};

export const fetchCalculatedValueDefinitions = () => async (dispatch: AppDispatch) => {
  try {
    const res = await apiService.listCalculatedValueDefinitions();
    dispatch(setCalculatedValueDefinitions(res.data ?? []));
  } catch {
    // non-fatal
  }
};

export default applicationSettingsSlice.reducer;
